import type { AIRequest, AIResponse } from '@/types'

// ─── Secure AI proxy transport ────────────────────────────────────────────────
// All AI calls go to this app's own /api/ai Pages Function. API keys live as
// Cloudflare Pages secrets (server-side only) — never in this bundle.

async function callServer(request: AIRequest): Promise<AIResponse> {
  const res = await fetch('/api/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt: request.prompt,
      systemPrompt: request.systemPrompt,
      maxTokens: request.maxTokens,
    }),
  })
  if (!res.ok) throw new Error(`AI proxy error ${res.status}`)
  const data = await res.json()
  return { content: data.content ?? '', provider: data.provider, model: data.model }
}

export async function callAI(request: AIRequest): Promise<AIResponse> {
  return callServer(request)
}

// ─── Yonder-specific AI helpers ───────────────────────────────────────────────

export async function generateItinerary(
  destination: string,
  dnaType: string,
  days: number,
  ageRange: string
): Promise<{ day: number; title: string; highlight: string }[]> {
  const systemPrompt = `You are Yonder, an expert family travel AI. You generate warm, exciting, child-friendly holiday itineraries. Always consider child safety, age-appropriate activities, and parent-friendly pacing. Respond ONLY with valid JSON, no markdown, no extra text.`

  const prompt = `Generate a ${days}-day family itinerary for ${destination}.
Family Travel DNA: ${dnaType}
Children's ages: ${ageRange}

Return a JSON array of ${days} objects, each with:
- "day": number
- "title": fun short title (max 6 words)  
- "highlight": one sentence describing the main child-friendly activity (max 25 words)

Example: [{"day":1,"title":"Castle magic and ice cream","highlight":"Explore the ancient hilltop fortress then cool off with local gelato at the market square."}]`

  const response = await callAI({ prompt, systemPrompt, maxTokens: 1000 })

  try {
    const clean = response.content.replace(/```json|```/g, '').trim()
    return JSON.parse(clean)
  } catch {
    // Fallback itinerary
    return Array.from({ length: days }, (_, i) => ({
      day: i + 1,
      title: `Day ${i + 1} adventure`,
      highlight: `Explore ${destination} with the whole family — a day full of discovery and fun.`,
    }))
  }
}

export async function generateFamilyDNASummary(
  dnaType: string,
  answers: string[],
  destinations: string[]
): Promise<string> {
  const systemPrompt = `You are Yonder, a warm and enthusiastic family travel AI. Write in a friendly, encouraging tone that speaks directly to parents. Keep it short — 2 sentences max.`

  const prompt = `A family completed our Travel DNA quiz. Their type is "${dnaType}". Their answers were: ${answers.join(', ')}.

The traveller's matched destinations are: ${destinations.join(', ')}. Reference ONLY these places by name if you name any place at all. Never mention any destination not in this list.

Write a personalised 2-sentence summary of this family's travel personality. Make it warm, specific and exciting. Address them directly as "your family".`

  const response = await callAI({ prompt, systemPrompt, maxTokens: 150 })
  return response.content.trim()
}
